import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  API_SCOPES,
  type ApiKeySummary,
  createKey,
  listKeys,
  revokeKey,
} from '@/features/apiKeys/api-keys-api';
import { useSession } from '@/features/auth/auth-store';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, Eye, EyeOff, KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/settings/api-keys')({
  component: ApiKeysPage,
});

function ApiKeysPage(): ReactElement | null {
  const { t } = useTranslation();
  const session = useSession();
  if (!session) return null;
  const orgId = session.activeOrg.id;
  const queryClient = useQueryClient();

  const keys = useQuery({
    queryKey: ['api-keys', orgId],
    queryFn: () => listKeys(orgId),
  });

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['exports:write']);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createKey(orgId, { name, scopes }),
    onSuccess: (data) => {
      toast.success(t('settings.apiKeys.createSuccess', { name: data.key.name }));
      setCreatedSecret(data.token);
      setShowSecret(false);
      setName('');
      queryClient.invalidateQueries({ queryKey: ['api-keys', orgId] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('settings.apiKeys.createFailed'));
    },
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) => revokeKey(orgId, keyId),
    onSuccess: () => {
      toast.success(t('settings.apiKeys.row.revokeSuccess'));
      queryClient.invalidateQueries({ queryKey: ['api-keys', orgId] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('settings.apiKeys.row.revokeFailed'));
    },
  });

  async function copySecret(): Promise<void> {
    if (!createdSecret) return;
    await navigator.clipboard.writeText(createdSecret);
    toast.success(t('settings.apiKeys.copySuccess'));
  }

  function toggleScope(scope: string): void {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }

  return (
    <div className="max-w-3xl space-y-8">
      <PageHeader
        eyebrow={t('app.layout.section.settings', { defaultValue: 'Settings' })}
        title={t('settings.apiKeys.title')}
        description={t('settings.apiKeys.description')}
      />

      {createdSecret ? (
        <Alert>
          <AlertTitle>{t('settings.apiKeys.newAlertTitle')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{t('settings.apiKeys.newAlertBody')}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md border bg-muted px-2 py-1 font-mono text-xs">
                {showSecret ? createdSecret : createdSecret.replace(/(.{18}).*/, '$1…')}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowSecret((v) => !v)}
              >
                {showSecret ? (
                  <EyeOff className="size-4" aria-hidden />
                ) : (
                  <Eye className="size-4" aria-hidden />
                )}
              </Button>
              <Button type="button" variant="outline" size="icon" onClick={copySecret}>
                <Copy className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCreatedSecret(null)}
              >
                {t('settings.apiKeys.hide')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-border/70 shadow-soft">
        <CardHeader>
          <div className="flex items-start gap-3">
            <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Plus className="size-4" aria-hidden />
            </span>
            <div className="space-y-1">
              <CardTitle className="text-base">{t('settings.apiKeys.newTitle')}</CardTitle>
              <CardDescription>{t('settings.apiKeys.newDescription')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="key-name">{t('settings.apiKeys.nameLabel')}</Label>
            <Input
              id="key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.apiKeys.namePlaceholder')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('settings.apiKeys.scopes')}</Label>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {API_SCOPES.map((scope) => {
                const active = scopes.includes(scope);
                return (
                  <label
                    key={scope}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                      active
                        ? 'border-primary/50 bg-primary/10 text-primary'
                        : 'border-border bg-background hover:border-primary/30'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="size-3.5 accent-primary"
                      checked={active}
                      onChange={() => toggleScope(scope)}
                    />
                    <code className="font-mono">{scope}</code>
                  </label>
                );
              })}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button
            onClick={() => {
              setError(null);
              create.mutate();
            }}
            disabled={!name || scopes.length === 0 || create.isPending}
          >
            {create.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            {t('settings.apiKeys.submit')}
          </Button>
        </CardFooter>
      </Card>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('settings.apiKeys.existingHeader')}
        </h2>
        {keys.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
        ) : (keys.data?.keys ?? []).length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title={t('settings.apiKeys.empty')}
            description={t('settings.apiKeys.emptyBody', {
              defaultValue: 'Create your first API key above to authenticate with the API.',
            })}
          />
        ) : (
          <ul className="space-y-2">
            {keys.data?.keys.map((k) => (
              <li key={k.id}>
                <KeyRow
                  keyRow={k}
                  revoking={revoke.isPending && revoke.variables === k.id}
                  onRevoke={() => revoke.mutate(k.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KeyRow({
  keyRow,
  revoking,
  onRevoke,
}: {
  keyRow: ApiKeySummary;
  revoking: boolean;
  onRevoke: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <KeyRound className="size-4" aria-hidden />
        </span>
        <div className="flex-1 space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            {keyRow.name}
            {keyRow.revokedAt ? (
              <Badge variant="destructive">{t('settings.apiKeys.row.revoked')}</Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            <code className="rounded bg-muted px-1 font-mono text-xs">{keyRow.prefix}…</code> ·{' '}
            {t('settings.apiKeys.row.scopesCount', { count: keyRow.scopes.length })} ·{' '}
            {t('settings.apiKeys.row.createdAt', { when: dateTime(keyRow.createdAt) })}
            {keyRow.lastUsedAt
              ? ` · ${t('settings.apiKeys.row.lastUsedAt', { when: dateTime(keyRow.lastUsedAt) })}`
              : ` · ${t('settings.apiKeys.row.notUsed')}`}
          </CardDescription>
          <div className="flex flex-wrap gap-1 pt-1">
            {keyRow.scopes.map((s) => (
              <Badge key={s} variant="secondary" className="font-mono text-[10px]">
                {s}
              </Badge>
            ))}
          </div>
        </div>
        {!keyRow.revokedAt ? (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={onRevoke}
            disabled={revoking}
          >
            {revoking ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="size-3.5" aria-hidden />
            )}
            {t('settings.apiKeys.row.revokeButton')}
          </Button>
        ) : null}
      </CardHeader>
    </Card>
  );
}
